import type { FunctionSignature } from '../decorators/decorators.types';
import { createEnhancedError } from '../decorators/decorators.utils';
import { MonitoringService } from '../monitoring.service';

/**
 * Common options for error capturing across decorators and hooks
 */
export interface ErrorCaptureContext {
  /** Function signature information */
  functionSignature?: FunctionSignature;
  /** Original error that was thrown */
  originalError: Error;
  /** Timestamp when error occurred */
  timestamp: string;
  /** Execution time in milliseconds */
  executionTime: number;
  /** Additional context data */
  additionalData?: Record<string, unknown>;
}

/**
 * Configuration for error capturing execution
 */
export interface ErrorCaptureConfig {
  /** Context name for the error */
  context: string;
  /** Additional context data to include */
  contextData?: Record<string, unknown>;
  /** Tags for filtering and grouping errors */
  tags?: Record<string, string | number | boolean>;
  /** Whether to re-throw the error after capturing */
  rethrow?: boolean;
  /** Custom error message to use instead of original */
  customMessage?: string;
  /** Function signature to include in error context */
  functionSignature?: FunctionSignature;
  /** Additional data to include in error context */
  additionalData?: Record<string, unknown>;
}

/**
 * Adds a breadcrumb for function execution start
 */
export function addExecutionStartBreadcrumb(
  contextName: string,
  argumentCount: number
): void {
  const monitoringService = MonitoringService.getInstance();

  monitoringService.addBreadcrumb({
    category: 'function',
    message: `Executing ${contextName}`,
    level: 'debug',
    data: {
      argumentCount,
    },
  });
}

/**
 * Adds a breadcrumb for successful function execution
 */
export function addExecutionSuccessBreadcrumb(
  contextName: string,
  executionTime: number
): void {
  const monitoringService = MonitoringService.getInstance();

  monitoringService.addBreadcrumb({
    category: 'function',
    message: `Successfully executed ${contextName}`,
    level: 'debug',
    data: {
      executionTime,
    },
  });
}

/**
 * Adds a breadcrumb for function execution error
 */
export function addExecutionErrorBreadcrumb(
  contextName: string,
  error: Error,
  executionTime: number,
  functionSignature?: FunctionSignature,
  additionalData?: Record<string, unknown>
): void {
  const monitoringService = MonitoringService.getInstance();
  const breadcrumbData: Record<string, unknown> = {
    errorType: error.name,
    executionTime,
  };

  if (functionSignature) {
    breadcrumbData['functionSignature'] = `${
      functionSignature.className || 'Unknown'
    }.${functionSignature.methodName}(${functionSignature.parameters})`;
  }

  if (additionalData) {
    breadcrumbData['additionalData'] = additionalData;
  }

  monitoringService.addBreadcrumb({
    category: 'error',
    message: `Error in ${contextName}: ${error.message}`,
    level: 'error',
    data: breadcrumbData,
  });
}

/**
 * Creates enhanced error context with function signature and timing information
 */
export function createErrorContext(
  originalError: Error,
  executionTime: number,
  functionSignature?: FunctionSignature,
  additionalData?: Record<string, unknown>,
  baseContextData?: Record<string, unknown>
): ErrorCaptureContext {
  const errorContext: ErrorCaptureContext = {
    originalError,
    timestamp: new Date().toISOString(),
    executionTime,
  };

  if (functionSignature) {
    errorContext.functionSignature = functionSignature;
  }

  if (additionalData) {
    errorContext.additionalData = additionalData;
  }

  // Merge with base context data
  return {
    ...baseContextData,
    ...errorContext,
  };
}

/**
 * Processes and captures an error with full context and monitoring
 */
export function captureErrorWithContext(
  originalError: Error,
  config: ErrorCaptureConfig,
  executionTime: number
): Error {
  const monitoringService = MonitoringService.getInstance();

  // Create error context
  const errorContext = createErrorContext(
    originalError,
    executionTime,
    config.functionSignature,
    config.additionalData,
    config.contextData
  );

  // Determine the error to capture
  let errorToCapture: Error;

  if (config.customMessage) {
    errorToCapture = new Error(config.customMessage);
  } else if (config.functionSignature) {
    errorToCapture = createEnhancedError(
      originalError,
      config.functionSignature
    );
  } else {
    errorToCapture = originalError;
  }

  // Set context for monitoring
  monitoringService.setContext(config.context, errorContext);

  // Capture the exception
  monitoringService.captureException(
    errorToCapture,
    config.context,
    errorContext,
    config.tags
  );

  // Add error breadcrumb
  addExecutionErrorBreadcrumb(
    config.context,
    errorToCapture,
    executionTime,
    config.functionSignature,
    config.additionalData
  );

  return errorToCapture;
}

export function executeWithErrorCaptureSync<TReturn>(
  functionToExecute: (...args: unknown[]) => TReturn,
  config: ErrorCaptureConfig,
  args: unknown[] = []
): TReturn {
  const startTime = Date.now();

  // Add start breadcrumb
  addExecutionStartBreadcrumb(config.context, args.length);

  try {
    // Execute the function
    const result = functionToExecute(...args);
    // Add success breadcrumb
    const executionTime = Date.now() - startTime;

    addExecutionSuccessBreadcrumb(config.context, executionTime);

    return result;
  } catch (error) {
    const executionTime = Date.now() - startTime;

    // Handle error capture
    const errorToCapture = captureErrorWithContext(
      error as Error,
      config,
      executionTime
    );

    // Rethrow based on configuration
    if (config.rethrow !== false) {
      throw errorToCapture;
    }

    // Return undefined if not rethrowing (for hooks)
    return undefined as TReturn;
  }
}
/**
 * Executes a function with full error monitoring lifecycle
 * This is the core execution flow used by both decorators and hooks
 */
export async function executeWithErrorCaptureAsync<TReturn>(
  functionToExecute: (...args: unknown[]) => Promise<TReturn> | TReturn,
  config: ErrorCaptureConfig,
  args: unknown[] = []
): Promise<TReturn> {
  const startTime = Date.now();

  // Add start breadcrumb
  addExecutionStartBreadcrumb(config.context, args.length);

  try {
    // Execute the function
    const result = await functionToExecute(...args);

    // Add success breadcrumb
    const executionTime = Date.now() - startTime;

    addExecutionSuccessBreadcrumb(config.context, executionTime);

    return result;
  } catch (error) {
    const executionTime = Date.now() - startTime;

    // Handle error capture
    const errorToCapture = captureErrorWithContext(
      error as Error,
      config,
      executionTime
    );

    // Rethrow based on configuration
    if (config.rethrow !== false) {
      throw errorToCapture;
    }

    // Return undefined if not rethrowing (for hooks)
    return undefined as TReturn;
  }
}
